import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssets } from '../services/assetService';
import { Asset, AssetState, FileType } from '../types/asset';
import { StatusBadge } from '../components/shared/StatusBadge';
import { FileTypeBadge } from '../components/shared/FileTypeBadge';
import { formatFileSize, formatRelativeTime, extractFileName } from '../utils/formatters';
import { ASSET_STATES } from '../utils/constants';
import { getFileTypes } from '../services/ruleService';
import { resources } from '../resources';
import { Search, Filter, ChevronLeft, ChevronRight, MoreVertical, X, SlidersHorizontal } from 'lucide-react';

export const AssetsList: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTypes, setSelectedTypes] = useState<FileType[]>([]);
  const [selectedStates, setSelectedStates] = useState<AssetState[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [fileTypes, setFileTypes] = useState<string[]>([]);

  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    let active = true;
    getFileTypes()
      .then((types) => {
        if (active) setFileTypes(types);
      })
      .catch(() => {
        if (active) setFileTypes([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      const { data, total } = await getAssets({
        search,
        types: selectedTypes,
        states: selectedStates,
        page,
        limit
      });
      setAssets(data);
      setTotal(total);
      setLoading(false);
    };

    const debounce = setTimeout(fetchAssets, 300);
    return () => clearTimeout(debounce);
  }, [search, selectedTypes, selectedStates, page]);

  const toggleType = (type: FileType) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
    setPage(1);
  };

  const toggleState = (state: AssetState) => {
    setSelectedStates(prev =>
      prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]
    );
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedStates([]);
    setSearch('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{resources.assetsList.title}</h1>
          <p className="text-muted-foreground">{resources.assetsList.subtitle}</p>
        </div>
        <button className="bg-card border border-border hover:bg-muted px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 text-foreground transition-colors shadow-sm">
           <SlidersHorizontal className="w-4 h-4" />
           {resources.common.exportCsv}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder={resources.common.searchPlaceholder}
              className="w-full pl-11 pr-4 py-3 bg-muted border-0 rounded-xl focus:ring-2 focus:ring-ring focus:bg-card transition-all text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-xl border flex items-center gap-2 text-sm font-medium transition-all ${showFilters ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-card border-border text-foreground hover:bg-muted'}`}
          >
            <Filter className="w-4 h-4" />
            {resources.assetsList.filters}
            {(selectedTypes.length > 0 || selectedStates.length > 0) && (
              <span className="bg-primary text-primary-foreground w-5 h-5 rounded-full flex items-center justify-center text-[10px]">
                {selectedTypes.length + selectedStates.length}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="pt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">{resources.assetsList.table.type}</p>
              <div className="flex flex-wrap gap-2">
                {fileTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedTypes.includes(type) ? 'bg-primary border-primary text-primary-foreground' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}
                  >
                    {type.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">{resources.assetsList.table.status}</p>
              <div className="flex flex-wrap gap-2">
                {ASSET_STATES.map(state => (
                  <button
                    key={state}
                    onClick={() => toggleState(state)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedStates.includes(state) ? 'bg-primary border-primary text-primary-foreground' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-muted-foreground hover:text-foreground font-medium flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                {resources.common.clearFilters}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assets Table */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetsList.table.fileName}</th>
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetsList.table.type}</th>
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetsList.table.status}</th>
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest text-right">{resources.assetsList.table.size}</th>
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{resources.assetsList.table.received}</th>
                <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={6} className="px-6 py-8 bg-muted/20"></td>
                  </tr>
                ))
              ) : assets.length > 0 ? (
                assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="hover:bg-primary/10 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/assets/${asset.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {extractFileName(asset.locationPath)}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground mt-0.5">{asset.id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <FileTypeBadge type={asset.fileType} />
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={asset.state} size="sm" />
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-muted-foreground font-medium tabular-nums">
                      {formatFileSize(asset.size)}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                      {formatRelativeTime(asset.receivedOn)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-all">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-3">
                       <Search className="w-10 h-10 text-border" />
                       <p className="font-medium">{resources.common.noAssetsFound}</p>
                       <button onClick={clearFilters} className="text-primary text-sm font-bold">{resources.common.clearFilters}</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-border bg-muted/50 flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">
              {resources.assetsList.pagination.showing} <span className="text-foreground">{(page - 1) * limit + 1}</span> {resources.assetsList.pagination.to} <span className="text-foreground">{Math.min(page * limit, total)}</span> {resources.assetsList.pagination.of} <span className="text-foreground">{total}</span> {resources.assetsList.pagination.assets}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-border rounded-lg hover:bg-card disabled:opacity-40 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 text-xs font-bold rounded-lg transition-all ${page === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-card'}`}
                    >
                      {p}
                    </button>
                  );
                })}
                {totalPages > 5 && <span className="text-muted-foreground">...</span>}
              </div>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-border rounded-lg hover:bg-card disabled:opacity-40 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
